import { useEffect, useState } from "react";
import axios from "../api/axios";
import { useLocation } from "react-router-dom";
import { hasAnyRole, isModerator as isModeratorActive } from "../utils/auth";

function FullReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStartYear, setFilterStartYear] = useState("");
  const [filterEndYear, setFilterEndYear] = useState("");
  const [canEditOthers, setCanEditOthers] = useState(false);
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  // Add print styles
  useEffect(() => {
    const printStyle = document.createElement("style");
    printStyle.innerHTML = `
      @media print {
        @page { margin: 8mm; }
        body { margin: 0; padding: 0; background: white; font-size: 9pt; line-height: 1.25; }
        .material-surface-strong { background: white !important; box-shadow: none !important; padding: 0 !important; }
        .print\\:hidden { display: none !important; }
        nav, header, footer, .sidebar { display: none !important; }
        button, [class*="btn"] { display: none !important; }
        .smooth-scroll-target { page-break-inside: auto; break-inside: auto; }
        h2 { font-size: 16pt; margin-top: 8pt; margin-bottom: 4pt; page-break-after: avoid; }
        h3 { font-size: 11pt; margin-top: 8pt; margin-bottom: 4pt; page-break-after: avoid; }
        table { width: 100%; border-collapse: collapse; margin: 4pt 0; page-break-inside: auto; break-inside: auto; }
        th, td { border: 1pt solid #000; padding: 3pt 4pt; text-align: left; vertical-align: top; }
        th { background: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
        tr { page-break-inside: avoid; break-inside: avoid; }
        .space-y-3 > * + *,
        .space-y-2 > * + *,
        .space-y-8 > * + * { margin-top: 4pt !important; }
        .bg-* { background: white !important; -webkit-print-color-adjust: none; }
      }
    `;
    document.head.appendChild(printStyle);
    return () => document.head.removeChild(printStyle);
  }, []);

  // Determine whether the current user may edit other users' records.
  useEffect(() => {
    let active = true;
    axios
      .get("/profile/edit-capability", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (active) setCanEditOthers(!!res.data?.can_edit_others);
      })
      .catch(() => {
        if (active) setCanEditOthers(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const location = useLocation();

  // Check if viewing someone else's report (for HR/Admin) vs your own.
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId");
  const facultyUserId = searchParams.get("facultyUserId");
  const isSelfReport = !profileId && !facultyUserId;

  const isModerator = isModeratorActive();
  const isSuper = hasAnyRole("super_admin");
  const baseVerify = hasAnyRole("hr", "admin", "super_admin") || isModerator;
  // Self-verification rule: you cannot verify your OWN records unless you are
  // super_admin. A moderator viewing their own report cannot verify; another
  // moderator (viewing this report as "someone else's") still can.
  const canVerifySelf = !isSelfReport || isSuper;
  const verifyAllowed = baseVerify && canVerifySelf;
  const canVerifyPersonal = verifyAllowed && (isSuper || !!report?.verification_permissions?.personal_information);
  const canVerifyExperience = verifyAllowed && (isSuper || !!report?.verification_permissions?.experience);
  const canVerifyEducation = verifyAllowed && (isSuper || !!report?.verification_permissions?.education);
  const canVerifyAcademic = verifyAllowed && (isSuper || !!report?.verification_permissions?.academic_information);
  const canVerifyResearch = verifyAllowed && (isSuper || !!report?.verification_permissions?.research_and_consultancy);
  const canVerifyFdp = verifyAllowed && (isSuper || !!report?.verification_permissions?.fdp_and_training);
  const canVerifyAwards = verifyAllowed && (isSuper || !!report?.verification_permissions?.awards_and_industry);
  const [rejectModal, setRejectModal] = useState({ open: false, tableName: "", recordId: "" });
  const [rejectReasonText, setRejectReasonText] = useState("");
  // Super-admin editable display Profile ID
  const [editingProfileId, setEditingProfileId] = useState(false);
  const [profileIdDraft, setProfileIdDraft] = useState("");
  const [savingProfileId, setSavingProfileId] = useState(false);

  const targetUserId = report?.user_id || report?.profile?.user_id || facultyUserId;
  const displayProfileId = report?.display_profile_id || report?.profile?.display_profile_id || "";

  const saveProfileId = async () => {
    const value = profileIdDraft.trim();
    if (!value) {
      alert("Profile ID cannot be empty.");
      return;
    }
    setSavingProfileId(true);
    try {
      const res = await axios.put(
        `/profile/admin/${targetUserId}/display-id`,
        { display_profile_id: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const saved = res.data?.display_profile_id || value;
      setReport((prev) => {
        if (!prev) return prev;
        const clone = { ...prev, display_profile_id: saved };
        if (clone.profile) clone.profile = { ...clone.profile, display_profile_id: saved };
        return clone;
      });
      setEditingProfileId(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update Profile ID");
    } finally {
      setSavingProfileId(false);
    }
  };
  const buildProtectedDocumentUrl = (documentPath) => {
    if (!documentPath) {
      return "#";
    }

    if (/^https?:\/\//i.test(documentPath)) {
      return documentPath;
    }

    return `${documentPath}?token=${encodeURIComponent(token || "")}`;
  };

  useEffect(() => {
    const mergeIndustryExperience = (baseReport, industryRows) => {
      if (!baseReport) return baseReport;
      const cloned = JSON.parse(JSON.stringify(baseReport));
      if (!cloned.experience) {
        cloned.experience = {};
      }
      cloned.experience.industry_experience = Array.isArray(industryRows) ? industryRows : [];
      return cloned;
    };

    const loadReport = async () => {
      // Super admin (acting as super_admin) cannot load their OWN report.
      if (hasAnyRole("super_admin") && !profileId && !facultyUserId) {
        setLoading(false);
        return;
      }
      try {
        let endpoint = "/profile/full-report";

        if (profileId) {
          endpoint = `/profile/admin/user-report/${profileId}`;
        } else if (facultyUserId && hasAnyRole("hod")) {
          endpoint = `/profile/hod/user-report/${facultyUserId}`;
        }

        const res = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        let nextReport = res.data;

        // Fallback for self report if backend full-report payload misses industry_experience.
        const isSelfReport = !profileId && !facultyUserId;
        const hasIndustryArray = Array.isArray(nextReport?.experience?.industry_experience);

        if (isSelfReport && !hasIndustryArray) {
          try {
            const industryRes = await axios.get("/profile/industry-experience", {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            nextReport = mergeIndustryExperience(nextReport, industryRes.data || []);
          } catch (industryErr) {
            console.warn("Industry fallback fetch failed:", industryErr?.response?.data || industryErr?.message);
            nextReport = mergeIndustryExperience(nextReport, []);
          }
        }

        setReport(nextReport);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch report:", err);
        setError(err.response?.data?.message || "Failed to load profile data");
        setLoading(false);
      }
    };

    loadReport();

  }, [token, profileId, facultyUserId, role]);

  // Super admin acting as super_admin may only view OTHERS' reports, never
  // their own. They must switch to a non-super_admin role to see their own.
  if (isSuper && isSelfReport) return (
    <div className="material-surface p-6">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <h3 className="text-amber-900 font-semibold mb-1">Your own report isn't available as Super Admin</h3>
        <p className="text-amber-800 text-sm">
          While acting as <span className="font-semibold">Super Admin</span> you can view other faculty's reports,
          not your own. Switch to another role (via the role menu, top right) to view your own report.
        </p>
      </div>
    </div>
  );

  if (loading) return (
    <div className="material-surface p-6">
      <p className="text-gray-600">Loading {profileId ? "employee" : "your"} complete profile...</p>
    </div>
  );

  if (error) return (
    <div className="material-surface p-6">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
        <h3 className="text-red-800 font-semibold mb-2">Error Loading Report</h3>
        <p className="text-red-600">{error}</p>
      </div>
    </div>
  );

  if (!report) return (
    <div className="material-surface p-6">
      <p className="text-gray-600">No profile data available</p>
    </div>
  );

  // Helper function to check if data exists and has length
  const hasData = (data) => data && Array.isArray(data) && data.length > 0;
  const isVerified = (item) => item?.verification_status === "verified";

  // Helper functions for formatting
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateYYYYMMDD = (dateString) => {
    if (!dateString) return "N/A";
    return dateString.split("T")[0];
  };

  const calculateExperienceDisplay = (dateEntry, dateExit, isCurrentAffiliated) => {
    if (!dateEntry) return "N/A";
    const start = new Date(dateEntry);
    const end = isCurrentAffiliated ? new Date() : (dateExit ? new Date(dateExit) : new Date());
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return "N/A";
    
    const diffMs = end - start;
    const totalMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30.44));
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    
    if (years === 0) return `${months} months`;
    if (months === 0) return `${years} year${years > 1 ? "s" : ""}`;
    return `${years} year${years > 1 ? "s" : ""} ${months} months`;
  };


  const handleOpenReject = (tableName, recordId) => {
    setRejectReasonText("");
    setRejectModal({ open: true, tableName, recordId });
  };

  const handleConfirmReject = async () => {
    const { tableName, recordId } = rejectModal;
    await updateVerificationStatus(tableName, recordId, "rejected", rejectReasonText);
    setRejectModal({ open: false, tableName: "", recordId: "" });
  };

  const updateVerificationStatus = async (tableName, recordId, nextStatus, rejectionReason = "") => {
    try {
      await axios.put(
        "/profile/admin/verification-status",
        {
          table_name: tableName,
          record_id: recordId,
          verification_status: nextStatus,
          rejection_reason: rejectionReason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setReport((prev) => {
        if (!prev) return prev;
        const clone = JSON.parse(JSON.stringify(prev));

        if (tableName === "personal_details") {
          if (clone.personal_information?.personal_details?.id === recordId) {
            clone.personal_information.personal_details.verification_status = nextStatus;
            clone.personal_information.personal_details.rejection_reason = rejectionReason;
          }
          return clone;
        }

        const sectionMap = [
          { path: ["education", "educational_qualifications"], table: "educational_qualifications" },
          { path: ["experience", "teaching_experience"], table: "teaching_experience" },
          { path: ["experience", "industry_experience"], table: "industry_experience" },
          { path: ["academic_information", "subjects_taught"], table: "subjects_taught" },
          { path: ["academic_information", "innovation_projects"], table: "innovation_projects" },
          { path: ["academic_information", "experimental_setups"], table: "experimental_setups" },
          { path: ["academic_information", "student_feedback"], table: "student_feedback" },
          { path: ["research_and_consultancy", "research_publications"], table: "research_publications" },
          { path: ["research_and_consultancy", "patents"], table: "patents" },
          { path: ["research_and_consultancy", "journal_roles"], table: "journal_roles" },
          { path: ["research_and_consultancy", "external_funding"], table: "external_funding" },
          { path: ["research_and_consultancy", "consultancy_revenue"], table: "consultancy_revenue" },
          { path: ["research_and_consultancy", "phd_scholars"], table: "phd_scholars" },
          { path: ["research_and_consultancy", "copyright_records"], table: "copyright_records" },
          { path: ["research_and_consultancy", "industrial_designs"], table: "industrial_designs" },
          { path: ["research_and_consultancy", "session_speaker_details"], table: "session_speaker_details" },
          { path: ["fdp_and_training", "fdp_organized"], table: "fdp_organized" },
          { path: ["fdp_and_training", "fdp_attended"], table: "fdp_attended" },
          { path: ["fdp_and_training", "fdp_resource_person"], table: "fdp_resource_person" },
          { path: ["fdp_and_training", "mooc_econtent_developed"], table: "mooc_econtent_developed" },
          { path: ["fdp_and_training", "swayam_mooc_certifications"], table: "swayam_mooc_certifications" },
          { path: ["fdp_and_training", "technical_skills"], table: "technical_skills" },
          { path: ["awards_and_industry", "awards"], table: "awards" },
          { path: ["awards_and_industry", "industry_connect"], table: "industry_connect" },
          { path: ["awards_and_industry", "professional_memberships"], table: "professional_memberships" },
        ];

        const target = sectionMap.find((s) => s.table === tableName);
        if (!target) return clone;

        const [lvl1, lvl2] = target.path;
        const list = clone[lvl1]?.[lvl2];
        if (!Array.isArray(list)) return clone;

        const index = list.findIndex((item) => item.id === recordId);
        if (index >= 0) {
          list[index].verification_status = nextStatus;
          list[index].rejection_reason = rejectionReason;
        }

        return clone;
      });
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update verification status");
    }
  };

  // Extract data from nested structure
  const personalDetailsRaw = report.personal_information?.personal_details;
  const personalDetails = Array.isArray(personalDetailsRaw)
    ? (personalDetailsRaw[0] || null)
    : personalDetailsRaw;
  const education = report.education?.educational_qualifications;
  const teachingExperience = report.experience?.teaching_experience;
  const industryExperience = report.experience?.industry_experience;
  const subjectsTaught = report.academic_information?.subjects_taught;
  const innovationProjects = report.academic_information?.innovation_projects;
  const experimentalSetups = report.academic_information?.experimental_setups;
  const studentFeedback = report.academic_information?.student_feedback;
  const researchPublications = report.research_and_consultancy?.research_publications;
  const patents = report.research_and_consultancy?.patents;
  const journalRoles = report.research_and_consultancy?.journal_roles;
  const externalFunding = report.research_and_consultancy?.external_funding;
  const consultancyRevenue = report.research_and_consultancy?.consultancy_revenue;
  const phdScholars = report.research_and_consultancy?.phd_scholars;
  const sessionSpeakerDetails = report.research_and_consultancy?.session_speaker_details;
  const copyrightRecords = report.research_and_consultancy?.copyright_records;
  const industrialDesigns = report.research_and_consultancy?.industrial_designs;
  const fdpOrganized = report.fdp_and_training?.fdp_organized;
  const fdpAttended = report.fdp_and_training?.fdp_attended;
  const fdpResourcePerson = report.fdp_and_training?.fdp_resource_person;
  const moocEcontentDeveloped = report.fdp_and_training?.mooc_econtent_developed;
  const swayamMoocCertifications = report.fdp_and_training?.swayam_mooc_certifications;
  const awards = report.awards_and_industry?.awards;
  const industryConnect = report.awards_and_industry?.industry_connect;
  const professionalMemberships = report.awards_and_industry?.professional_memberships;
  const technicalSkills = report.fdp_and_training?.technical_skills;
  const sectionVisibility = report.section_visibility || {};
  const hiddenSections = Object.keys(sectionVisibility).filter((sectionKey) => sectionVisibility[sectionKey] === false);

  // ===== YEAR FILTER =====

  // Helper: extract the start year from an academic_year string. Handles both
  // 4-digit ("2023-24" → 2023) and 2-digit ("23-24" → 2023) formats.
  const getStartYearFromAcademicYear = (ay) => {
    if (!ay) return null;
    const match = String(ay).match(/(\d{4}|\d{2})/);
    if (!match) return null;
    let y = parseInt(match[1], 10);
    if (y < 100) y += 2000; // "23" → 2023
    return isNaN(y) ? null : y;
  };

  // Helper: extract year from a date string
  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d.getFullYear();
  };

  // Generic filter: check if a record falls within the year range
  const isInYearRange = (record) => {
    if (!filterStartYear && !filterEndYear) return true;
    const start = filterStartYear ? parseInt(filterStartYear, 10) : null;
    const end = filterEndYear ? parseInt(filterEndYear, 10) : null;

    // Try multiple year fields
    const yearCandidates = [
      getStartYearFromAcademicYear(record.academic_year),
      record.publication_year ? parseInt(record.publication_year, 10) : null,
      record.project_year ? parseInt(record.project_year, 10) : null,
      record.award_year ? parseInt(record.award_year, 10) : null,
      record.year ? parseInt(record.year, 10) : null,
      record.starting_year ? parseInt(record.starting_year, 10) : null,
      getYearFromDate(record.date_of_entry),
      getYearFromDate(record.start_date),
      getYearFromDate(record.date_of_filing),
      getYearFromDate(record.date_of_registration),
      getYearFromDate(record.date_of_grant),
      getYearFromDate(record.consultancy_date),
      getYearFromDate(record.date),
    ];

    const validYears = yearCandidates.filter((y) => y !== null && !isNaN(y));
    if (validYears.length === 0) return true; // No year info, show by default

    // Record passes if ANY of its year values fall within range
    return validYears.some((y) => {
      if (start && end) return y >= start && y <= end;
      if (start) return y >= start;
      if (end) return y <= end;
      return true;
    });
  };

  // Rank a record for latest-first sorting. We sort primarily by academic year,
  // then by any other year signal (publication/award/filing/etc.), then by id,
  // so records always appear newest-first and ties are broken deterministically.
  const getSortRank = (record) => {
    if (!record) return -Infinity;

    const academicYear = getStartYearFromAcademicYear(record.academic_year);

    // IMPORTANT: never use created_at / updated_at here. Those reflect when the
    // row was entered, not the academic/publication year, and would make the
    // list appear ordered by "most recently updated" instead of by year.
    const otherYearCandidates = [
      record.publication_year ? parseInt(record.publication_year, 10) : null,
      record.project_year ? parseInt(record.project_year, 10) : null,
      record.award_year ? parseInt(record.award_year, 10) : null,
      record.year ? parseInt(record.year, 10) : null,
      record.starting_year ? parseInt(record.starting_year, 10) : null,
      record.year_of_passing ? parseInt(record.year_of_passing, 10) : null,
      getYearFromDate(record.date_of_entry),
      getYearFromDate(record.start_date),
      getYearFromDate(record.date_of_filing),
      getYearFromDate(record.date_of_registration),
      getYearFromDate(record.date_of_grant),
      getYearFromDate(record.consultancy_date),
      getYearFromDate(record.date),
    ].filter((y) => y !== null && !isNaN(y));

    const otherYear = otherYearCandidates.length ? Math.max(...otherYearCandidates) : null;
    // Primary sort key = academic year (fall back to the best other year signal
    // when there is no academic year on the record).
    const primaryYear = academicYear !== null ? academicYear : (otherYear !== null ? otherYear : 0);
    const secondaryYear = otherYear !== null ? otherYear : 0;
    const idPart = record.id ? Number(record.id) : 0;

    // Composite rank: academic year dominates, then other-year, then id.
    return primaryYear * 1e10 + secondaryYear * 1e5 + idPart;
  };

  // Sort a copy of the list in descending order (latest first).
  const sortDescending = (data) => {
    if (!data || !Array.isArray(data)) return data;
    return [...data].sort((a, b) => getSortRank(b) - getSortRank(a));
  };

  // Apply year filter + descending sort to arrays.
  const filterData = (data) => {
    if (!data || !Array.isArray(data)) return sortDescending(data);
    const filtered =
      !filterStartYear && !filterEndYear ? data : data.filter(isInYearRange);
    return sortDescending(filtered);
  };

  // Filtered versions of all data arrays (only sections with academic_year)
  const filteredSubjectsTaught = filterData(subjectsTaught);
  const filteredInnovationProjects = filterData(innovationProjects);
  const filteredExperimentalSetups = filterData(experimentalSetups);
  const filteredStudentFeedback = filterData(studentFeedback);
  const filteredResearchPublications = filterData(researchPublications);
  const filteredPatents = filterData(patents);
  const filteredJournalRoles = filterData(journalRoles);
  const filteredExternalFunding = filterData(externalFunding);
  const filteredConsultancyRevenue = filterData(consultancyRevenue);
  const filteredPhdScholars = filterData(phdScholars);
  const filteredSessionSpeakerDetails = filterData(sessionSpeakerDetails);
  const filteredCopyrightRecords = filterData(copyrightRecords);
  const filteredIndustrialDesigns = filterData(industrialDesigns);
  const filteredFdpOrganized = filterData(fdpOrganized);
  const filteredFdpAttended = filterData(fdpAttended);
  const filteredFdpResourcePerson = filterData(fdpResourcePerson);
  const filteredMoocEcontentDeveloped = filterData(moocEcontentDeveloped);
  const filteredSwayamMoocCertifications = filterData(swayamMoocCertifications);
  const filteredAwards = filterData(awards);
  const filteredIndustryConnect = filterData(industryConnect);
  const filteredTechnicalSkills = filterData(technicalSkills);
  // Education, Teaching Experience, Industry Experience are NOT filtered by
  // academic year, but are still sorted latest-first.
  const filteredTeachingExperience = sortDescending(teachingExperience);
  const filteredIndustryExperience = sortDescending(industryExperience);
  const filteredProfessionalMemberships = sortDescending(professionalMemberships);
  const sortedEducation = sortDescending(education);

  return (
    <div className="material-surface-strong p-4 space-y-3">
      <div className="mb-4 border-b border-slate-200 dark:border-slate-800 pb-2 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Complete Profile Report</h2>
          {(profileId || facultyUserId) && report.user_email && (
            <p className="text-gray-600 text-sm mt-1">Profile: <span className="font-semibold text-blue-700">{report.user_email}</span></p>
          )}
          {displayProfileId && (
            <div className="mt-1 flex items-center gap-2 text-sm">
              <span className="text-gray-600">Profile ID:</span>
              {editingProfileId ? (
                <span className="inline-flex items-center gap-2 print:hidden">
                  <input
                    type="text"
                    value={profileIdDraft}
                    onChange={(e) => setProfileIdDraft(e.target.value)}
                    placeholder="e.g. FAC-2026-011"
                    maxLength={50}
                    className="w-44 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-400 outline-none font-mono"
                  />
                  <button
                    onClick={saveProfileId}
                    disabled={savingProfileId}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded-md"
                  >
                    {savingProfileId ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => setEditingProfileId(false)}
                    disabled={savingProfileId}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold px-3 py-1 rounded-md"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <>
                  <span className="font-mono font-semibold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{displayProfileId}</span>
                  {isSuper && targetUserId && (
                    <button
                      onClick={() => {
                        setProfileIdDraft(displayProfileId);
                        setEditingProfileId(true);
                      }}
                      className="print:hidden text-xs text-blue-600 hover:underline font-medium"
                      title="Change this Profile ID (super admin)"
                    >
                      ✏️ Change
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 print:hidden">
          {(profileId || facultyUserId) && canEditOthers && (report.user_id || report.profile?.user_id) && (
            <a
              href={`/profile?act_as=${report.user_id || report.profile?.user_id}`}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-4 rounded-lg shadow-lg transform hover:scale-105 transition-all text-sm"
              title="Edit this user's profile on their behalf"
            >
              ✏️ Edit this Profile
            </a>
          )}
          <button 
            onClick={() => window.print()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-lg transform hover:scale-105 transition-all text-sm"
            title="Print this report in resume/PDF format"
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Year Filter */}
      <div className="print:hidden mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-gray-700">Filter by Year:</span>
        <input
          type="number"
          placeholder="Start Year"
          value={filterStartYear}
          onChange={(e) => setFilterStartYear(e.target.value)}
          className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
          min="2000"
          max="2100"
        />
        <span className="text-gray-500 text-sm">to</span>
        <input
          type="number"
          placeholder="End Year"
          value={filterEndYear}
          onChange={(e) => setFilterEndYear(e.target.value)}
          className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
          min="2000"
          max="2100"
        />
        {(filterStartYear || filterEndYear) && (
          <button
            onClick={() => { setFilterStartYear(""); setFilterEndYear(""); }}
            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
          >
            ✕ Clear
          </button>
        )}
        {(filterStartYear || filterEndYear) && (
          <span className="text-xs text-blue-600 font-medium ml-2">
            Showing data from {filterStartYear || "..."} to {filterEndYear || "..."}
          </span>
        )}
      </div>

      {hiddenSections.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-amber-800 font-semibold">Role-based visibility is active</p>
          <p className="text-amber-700 text-sm mt-1">
            Some sections are hidden for your role based on Roles & Access settings.
          </p>
        </div>
      )}

      {/* Basic Info Section */}
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">📧 Basic Info</h3>
        {report.profile || report.user_email ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 space-y-1 text-xs md:text-sm">
            <p className="text-gray-700"><span className="font-semibold">Email:</span> {report.profile?.email || report.user_email}</p>
            {report.profile?.role && (
              <p className="text-gray-700"><span className="font-semibold">Role:</span> <span className="inline-block px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs">{report.profile.role}</span></p>
            )}
            {report.is_submitted !== undefined && (
              <p className="text-gray-700"><span className="font-semibold">Status:</span> 
                <span className={`inline-block ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${report.is_submitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {report.is_submitted ? '✅ Submitted' : '⏳ Draft'}
                </span>
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-2">
            <p className="text-gray-500 italic text-xs">Details not available</p>
          </div>
        )}
      </div>

      {/* Personal Details - 2 Column Compact Format */}
      <div className="smooth-scroll-target mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">👤 Personal Details</h3>
        {personalDetails ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            {personalDetails.photo_url && (
              <div className="flex justify-center mb-2">
                <img
                  src={personalDetails.photo_url}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-blue-200 shadow-sm"
                />
              </div>
            )}
            <VerificationBadge
                          status={personalDetails.verification_status}
                          canVerify={canVerifyPersonal}
                          onVerify={() => updateVerificationStatus("personal_details", personalDetails.id, "verified")}
                          onUnverify={() => updateVerificationStatus("personal_details", personalDetails.id, "unverified")}
                          onReject={() => handleOpenReject("personal_details", personalDetails.id)}
                          rejectionReason={personalDetails.rejection_reason}
                        />
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <tbody>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold w-1/4">Full Name</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.full_name || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold w-1/4">DOB</td>
                  <td className="border border-gray-300 px-1 py-0.5">{formatDateDDMMYYYY(personalDetails.date_of_birth)}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Parent Name</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.parent_name || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Gender</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.gender || 'N/A'}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Phone</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.phone_number || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Aadhar</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.aadhar_card || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">PAN</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.pan_card || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Marital Status</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.marital_status || 'N/A'}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Category</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.category || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Department</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.department || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Designation</td>
                  <td className="border border-gray-300 px-1 py-0.5">{personalDetails.current_designation || 'N/A'}</td>
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Joining Date</td>
                  <td className="border border-gray-300 px-1 py-0.5">{formatDateYYYYMMDD(personalDetails.date_of_joining)}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-1 py-0.5 font-semibold">Address</td>
                  <td colSpan="3" className="border border-gray-300 px-1 py-0.5">{personalDetails.address || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-1">
            <p className="text-gray-500 italic text-xs">Details not available</p>
          </div>
        )}
      </div>


      {/* Education Section */}
      <div className="smooth-scroll-target mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">🎓 Education</h3>
        {hasData(education) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Qualification</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Specialization & Division</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">University</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year / Status</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">%</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyEducation && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {sortedEducation.map((edu, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5"><strong>{edu.qualification_type === "Other" ? edu.qualification_other : edu.qualification_type}</strong></td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <div>{edu.specialization || '-'}</div>
                      {edu.division && <div className="text-[10px] text-gray-500">Division: {edu.division}</div>}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{edu.university || 'N/A'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{edu.is_pursuing ? <span className="text-blue-600 font-semibold">Pursuing</span> : edu.year_of_passing || 'N/A'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{edu.percentage || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={edu.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {edu.verification_status}
                      </span>
                    </td>
                    {canVerifyEducation && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={edu.verification_status}
                          canVerify={canVerifyEducation}
                          onVerify={() => updateVerificationStatus("educational_qualifications", edu.id, "verified")}
                          onUnverify={() => updateVerificationStatus("educational_qualifications", edu.id, "unverified")}
                          onReject={() => handleOpenReject("educational_qualifications", edu.id)}
                          rejectionReason={edu.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Teaching Experience */}
      <div className="smooth-scroll-target mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">👨‍🏫 Teaching Experience</h3>
        {hasData(filteredTeachingExperience) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">College</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Designation</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Status</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Entry</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Exit</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Experience</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Verification</th>
                  {canVerifyExperience && <th className="border border-gray-300 px-1 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTeachingExperience.map((exp, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.college_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.designation || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.status || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{formatDateYYYYMMDD(exp.date_of_entry)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.is_current_affiliated ? "Present" : formatDateYYYYMMDD(exp.date_of_exit)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{calculateExperienceDisplay(exp.date_of_entry, exp.date_of_exit, exp.is_current_affiliated)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">
                      <span className={exp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {exp.verification_status}
                      </span>
                    </td>
                    {canVerifyExperience && (
                      <td className="border border-gray-300 px-1 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={exp.verification_status}
                          canVerify={canVerifyExperience}
                          onVerify={() => updateVerificationStatus("teaching_experience", exp.id, "verified")}
                          onUnverify={() => updateVerificationStatus("teaching_experience", exp.id, "unverified")}
                          onReject={() => handleOpenReject("teaching_experience", exp.id)}
                          rejectionReason={exp.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-2">
            <p className="text-gray-500 italic text-xs">Details not available</p>
          </div>
        )}
      </div>

      {/* Industry Experience */}
      <div className="smooth-scroll-target mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">💼 Industry Experience</h3>
        <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
          <table className="w-full border-collapse border border-gray-300 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Company</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Designation</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Entry</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Exit</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Experience</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Status</th>
                {canVerifyExperience && <th className="border border-gray-300 px-1 py-0.5 text-center font-semibold print:hidden">Verify</th>}
              </tr>
            </thead>
            <tbody>
              {hasData(filteredIndustryExperience) ? (
                filteredIndustryExperience.map((exp, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.company_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.designation || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{formatDateYYYYMMDD(exp.date_of_entry)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{exp.is_current_affiliated ? "Present" : formatDateYYYYMMDD(exp.date_of_exit)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{calculateExperienceDisplay(exp.date_of_entry, exp.date_of_exit, exp.is_current_affiliated)}</td>
                    <td className="border border-gray-300 px-1 py-0.5">
                      <span className={exp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {exp.verification_status}
                      </span>
                    </td>
                    {canVerifyExperience && (
                      <td className="border border-gray-300 px-1 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={exp.verification_status}
                          canVerify={canVerifyExperience}
                          onVerify={() => updateVerificationStatus("industry_experience", exp.id, "verified")}
                          onUnverify={() => updateVerificationStatus("industry_experience", exp.id, "unverified")}
                          onReject={() => handleOpenReject("industry_experience", exp.id)}
                          rejectionReason={exp.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="border border-gray-300 px-1 py-1 text-gray-500 italic text-xs" colSpan={canVerifyAcademic ? 7 : 6}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subjects Taught */}
      <div className="smooth-scroll-target mb-2">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">📚 Subjects Taught</h3>
        {hasData(filteredSubjectsTaught) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Subject</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Program</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Semester</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Category</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyAcademic && <th className="border border-gray-300 px-1 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSubjectsTaught.map((sub, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1 py-0.5"><strong>{sub.subject_name || 'N/A'}</strong></td>
                    <td className="border border-gray-300 px-1 py-0.5">{sub.program_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{sub.semester || '-'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{sub.subject_category || '-'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">{sub.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1 py-0.5">
                      <span className={sub.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {sub.verification_status}
                      </span>
                    </td>
                    {canVerifyAcademic && (
                      <td className="border border-gray-300 px-1 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={sub.verification_status}
                          canVerify={canVerifyAcademic}
                          onVerify={() => updateVerificationStatus("subjects_taught", sub.id, "verified")}
                          onUnverify={() => updateVerificationStatus("subjects_taught", sub.id, "unverified")}
                          onReject={() => handleOpenReject("subjects_taught", sub.id)}
                          rejectionReason={sub.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-2">
            <p className="text-gray-500 italic text-xs">Details not available</p>
          </div>
        )}
      </div>

      {/* Innovation Projects */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">💡 Innovation Projects</h3>
        {hasData(filteredInnovationProjects) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Project Name</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Level</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Role</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Rank</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyAcademic && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredInnovationProjects.map((proj, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5"><strong>{proj.project_name || 'N/A'}</strong></td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{proj.level || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{proj.role || proj.mentor_role || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{proj.project_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{proj.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{proj.rank_awarded || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={proj.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {proj.verification_status}
                      </span>
                    </td>
                    {canVerifyAcademic && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={proj.verification_status}
                          canVerify={canVerifyAcademic}
                          onVerify={() => updateVerificationStatus("innovation_projects", proj.id, "verified")}
                          onUnverify={() => updateVerificationStatus("innovation_projects", proj.id, "unverified")}
                          onReject={() => handleOpenReject("innovation_projects", proj.id)}
                          rejectionReason={proj.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Experimental Setups */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🔧 Experimental Setups</h3>
        {hasData(filteredExperimentalSetups) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Setup Name</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Laboratory</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Semester</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Expenses</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyAcademic && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredExperimentalSetups.map((setup, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5"><strong>{setup.setup_name || 'N/A'}</strong></td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{setup.laboratory_name || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{setup.semester || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{setup.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">₹{setup.expenses || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={setup.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {setup.verification_status}
                      </span>
                    </td>
                    {canVerifyAcademic && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={setup.verification_status}
                          canVerify={canVerifyAcademic}
                          onVerify={() => updateVerificationStatus("experimental_setups", setup.id, "verified")}
                          onUnverify={() => updateVerificationStatus("experimental_setups", setup.id, "unverified")}
                          onReject={() => handleOpenReject("experimental_setups", setup.id)}
                          rejectionReason={setup.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Student Feedback */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📊 Student Feedback</h3>
        {hasData(filteredStudentFeedback) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Subject</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Semester</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Score</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyAcademic && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredStudentFeedback.map((feedback, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5"><strong>{feedback.subject_name || 'N/A'}</strong></td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{feedback.semester || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{feedback.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{feedback.feedback_score || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5 text-xs">
                      <span className={feedback.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {feedback.verification_status}
                      </span>
                    </td>
                    {canVerifyAcademic && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={feedback.verification_status}
                          canVerify={canVerifyAcademic}
                          onVerify={() => updateVerificationStatus("student_feedback", feedback.id, "verified")}
                          onUnverify={() => updateVerificationStatus("student_feedback", feedback.id, "unverified")}
                          onReject={() => handleOpenReject("student_feedback", feedback.id)}
                          rejectionReason={feedback.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Research Identifiers - placed above Research Publications */}
      {personalDetails && (personalDetails.google_scholar_id || personalDetails.scopus_id || personalDetails.wos_id || personalDetails.orcid_id) && (
        <div className="smooth-scroll-target mb-2">
          <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5">🔬 Research Identifiers</h3>
          <div className="flex flex-wrap gap-3 p-2 bg-white border border-gray-300 rounded-lg">
            {personalDetails.google_scholar_id && (
              <a href={`https://scholar.google.com/citations?user=${personalDetails.google_scholar_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                📚 Google Scholar ID: <span className="font-semibold">{personalDetails.google_scholar_id}</span>
              </a>
            )}
            {personalDetails.scopus_id && (
              <a href={`https://www.scopus.com/authid/detail.uri?authorId=${personalDetails.scopus_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-xs font-medium text-orange-700 hover:bg-orange-100 transition-colors">
                🔍 Scopus ID: <span className="font-semibold">{personalDetails.scopus_id}</span>
              </a>
            )}
            {personalDetails.wos_id && (
              <a href={`https://www.webofscience.com/wos/author/record/${personalDetails.wos_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors">
                🌐 Researcher ID (WoS): <span className="font-semibold">{personalDetails.wos_id}</span>
              </a>
            )}
            {personalDetails.orcid_id && (
              <a href={`https://orcid.org/${personalDetails.orcid_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700 hover:bg-green-100 transition-colors">
                🆔 ORCID: <span className="font-semibold">{personalDetails.orcid_id}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Research Publications */}
      {/* Publications Summary (counts) — VERIFIED records only */}
      {hasData(filteredResearchPublications) && filteredResearchPublications.some((r) => r.verification_status === "verified") && (() => {
        const pubs = filteredResearchPublications.filter((r) => r.verification_status === "verified");
        const total = pubs.length;
        const cat = (r) => (r.category || "").toLowerCase();
        const journals = pubs.filter((r) => cat(r) !== "conference" && cat(r) !== "book-chapter").length;
        const conferences = pubs.filter((r) => cat(r) === "conference").length;
        const bookChapters = pubs.filter((r) => cat(r) === "book-chapter").length;
        // Consider both the indexing field and any indexing_tags (e.g. SCI/SCIE).
        const idx = (r) => `${r.indexing || ""} ${r.indexing_tags || ""}`.toLowerCase();
        const isScopus = (r) => idx(r).includes("scopus");
        // WoS/SCI: count ANY record whose indexing mentions wos or sci — including
        // combined ones like "Scopus + WOS".
        const isWos = (r) => idx(r).includes("wos") || idx(r).includes("sci");
        const scopus = pubs.filter(isScopus).length;
        const wos = pubs.filter(isWos).length;
        // Non-Scopus = has neither scopus nor wos/sci indexing.
        const nonScopus = pubs.filter((r) => !isScopus(r) && !isWos(r)).length;
        const pad = (n) => String(n).padStart(2, "0");
        const summaryCell = "border border-gray-800 px-3 py-1";
        return (
          <div className="smooth-scroll-target mb-3 flex flex-wrap gap-6">
            <table className="border-collapse text-sm">
              <tbody>
                <tr>
                  <td className={`${summaryCell} font-semibold`}>Total Publications</td>
                  <td className={`${summaryCell} font-bold text-center w-12`}>{total}</td>
                </tr>
                <tr>
                  <td className={`${summaryCell} text-right`}>Journals</td>
                  <td className={`${summaryCell} font-bold text-center`}>{journals}</td>
                </tr>
                <tr>
                  <td className={`${summaryCell} text-right`}>Conferences</td>
                  <td className={`${summaryCell} font-bold text-center`}>{conferences}</td>
                </tr>
                <tr>
                  <td className={`${summaryCell} text-right`}>Book Chapters</td>
                  <td className={`${summaryCell} font-bold text-center`}>{bookChapters}</td>
                </tr>
              </tbody>
            </table>
            <table className="border-collapse text-sm self-start">
              <tbody>
                <tr>
                  <td className={`${summaryCell} font-semibold`}>Scopus Indexed</td>
                  <td className={`${summaryCell} font-bold text-center w-12`}>{pad(scopus)}</td>
                </tr>
                <tr>
                  <td className={`${summaryCell} font-semibold`}>Web of Science</td>
                  <td className={`${summaryCell} font-bold text-center`}>{pad(wos)}</td>
                </tr>
                <tr>
                  <td className={`${summaryCell} font-semibold`}>Non Scopus</td>
                  <td className={`${summaryCell} font-bold text-center`}>{pad(nonScopus)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📝 Research Publications</h3>
        {hasData(filteredResearchPublications) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Type</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Title</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Journal / Venue</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">SVPCET Affiliation</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Indexing</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredResearchPublications.map((res, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 text-xs font-semibold uppercase">
                      {res.category === 'journal' ? 'Journal' : res.category === 'conference' ? 'Conference' : res.category === 'book-chapter' ? 'Book Chapter' : 'Publication'}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {res.url || res.doi ? (
                        <a href={res.url || `https://doi.org/${res.doi}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline font-semibold">
                          {res.title || 'N/A'}
                        </a>
                      ) : (
                        <strong>{res.title || 'N/A'}</strong>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{res.journal_name || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{res.publication_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{res.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{res.svpcet_affiliation || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {res.indexing ? (
                        <span className="text-xs font-medium">
                          {res.indexing}{res.indexing_tags ? `(${res.indexing_tags})` : ''}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={res.verification_status === "verified" ? "text-green-600 font-semibold" : res.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {res.verification_status || "unverified"}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={res.verification_status || "unverified"}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("research_publications", res.id, "verified")}
                          onUnverify={() => updateVerificationStatus("research_publications", res.id, "unverified")}
                          onReject={() => handleOpenReject("research_publications", res.id)}
                          rejectionReason={res.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Patents */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🔬 Patents and Design</h3>
        {hasData(filteredPatents) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Patent details</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Numbers</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Dates & Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Indexing</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPatents.map((pat, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <strong className="block">{pat.patent_name || "N/A"}</strong>
                      <span className="text-[10px] text-gray-500">Type: {pat.patent_type || "N/A"} | Status: {pat.patent_status || "N/A"}</span>
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <div className="text-[10px]">App: {pat.application_no || "N/A"}</div>
                      <div className="text-[10px]">File: {pat.filing_no || "N/A"}</div>
                      {pat.grant_no && <div className="text-[10px]">Grant: {pat.grant_no}</div>}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <div className="text-[10px]">Filed: {formatDateDDMMYYYY(pat.date_of_filing)}</div>
                      <div className="text-[10px]">Pub: {formatDateDDMMYYYY(pat.date_published)}</div>
                      {pat.grant_date && <div className="text-[10px]">Grant: {formatDateDDMMYYYY(pat.grant_date)}</div>}
                      {pat.academic_year && <div className="text-[10px] text-blue-600 font-semibold">AY: {pat.academic_year}</div>}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{pat.patent_indexing || "Others"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={pat.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {pat.verification_status}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {pat.document_link ? (
                        <a href={buildProtectedDocumentUrl(pat.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          View Document
                        </a>
                      ) : "-"}
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={pat.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("patents", pat.id, "verified")}
                          onUnverify={() => updateVerificationStatus("patents", pat.id, "unverified")}
                          onReject={() => handleOpenReject("patents", pat.id)}
                          rejectionReason={pat.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Copyright Records */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📝 Copyright Records</h3>
        {hasData(filteredCopyrightRecords) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Copyright Name</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Date of Registration</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCopyrightRecords.map((cop, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {cop.copyright_name || "N/A"}
                      {cop.document_link && (
                        <a href={buildProtectedDocumentUrl(cop.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cop.status || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{formatDateDDMMYYYY(cop.date_of_registration)}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cop.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={cop.verification_status === "verified" ? "text-green-600 font-semibold" : cop.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {cop.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={cop.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("copyright_records", cop.id, "verified")}
                          onUnverify={() => updateVerificationStatus("copyright_records", cop.id, "unverified")}
                          onReject={() => handleOpenReject("copyright_records", cop.id)}
                          rejectionReason={cop.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Industrial Designs removed from report */}

      {/* Journal Roles */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📰 Journal Roles</h3>
        {hasData(filteredJournalRoles) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Journal</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Role</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Level</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredJournalRoles.map((role, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <strong className="block">{role.journal_name || "N/A"}</strong>
                      {role.publisher && <span className="text-[10px] text-gray-500">Publisher: {role.publisher}</span>}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{role.role_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{role.level || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{role.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {role.letter_details ? (
                        <a href={role.letter_details} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          View Document
                        </a>
                      ) : "-"}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={role.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {role.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={role.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("journal_roles", role.id, "verified")}
                          onUnverify={() => updateVerificationStatus("journal_roles", role.id, "unverified")}
                          onReject={() => handleOpenReject("journal_roles", role.id)}
                          rejectionReason={role.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* External Funding */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">💰 External Funding</h3>
        {hasData(filteredExternalFunding) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Agency</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Type</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Amount</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredExternalFunding.map((fund, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{fund.agency_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fund.agency_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">₹{fund.amount || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fund.year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fund.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {fund.letter_details ? (
                        <a href={fund.letter_details} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          View Document
                        </a>
                      ) : "-"}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={fund.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {fund.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={fund.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("external_funding", fund.id, "verified")}
                          onUnverify={() => updateVerificationStatus("external_funding", fund.id, "unverified")}
                          onReject={() => handleOpenReject("external_funding", fund.id)}
                          rejectionReason={fund.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Consultancy Revenue */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">💼 Consultancy Revenue</h3>
        {hasData(filteredConsultancyRevenue) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Industry</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Type</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Amount</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Date</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredConsultancyRevenue.map((cons, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{cons.industry_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cons.consultancy_type || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">₹{cons.amount || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cons.year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cons.academic_year || '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{cons.consultancy_date ? formatDateDDMMYYYY(cons.consultancy_date) : '-'}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {cons.letter_details ? (
                        <a href={cons.letter_details} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Document
                        </a>
                      ) : "-"}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={cons.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {cons.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={cons.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("consultancy_revenue", cons.id, "verified")}
                          onUnverify={() => updateVerificationStatus("consultancy_revenue", cons.id, "unverified")}
                          onReject={() => handleOpenReject("consultancy_revenue", cons.id)}
                          rejectionReason={cons.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* PhD Scholars */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🎓 PhD Scholars</h3>
        {hasData(filteredPhdScholars) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Scholar</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Starting Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Ending Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Thesis</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Notification</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPhdScholars.map((scholar, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {scholar.scholar_name || "N/A"}
                      {scholar.document_link && (
                        <a href={buildProtectedDocumentUrl(scholar.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.status || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.starting_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.is_pursuing ? "Pursuing" : (scholar.ending_year || "N/A")}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.thesis_title || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{scholar.notification_details || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={scholar.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {scholar.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={scholar.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("phd_scholars", scholar.id, "verified")}
                          onUnverify={() => updateVerificationStatus("phd_scholars", scholar.id, "unverified")}
                          onReject={() => handleOpenReject("phd_scholars", scholar.id)}
                          rejectionReason={scholar.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Session chair / keynote speaker details */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🎤 Session Chair / Keynote Speaker Details</h3>
        {hasData(filteredSessionSpeakerDetails) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Role</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Conference Name</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Institute</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">NIRF Rank</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Mode</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Level</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Letter No</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Date</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSessionSpeakerDetails.map((record, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {record.role || "N/A"}
                      {record.document_link && (
                        <a href={buildProtectedDocumentUrl(record.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.name_of_conference || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.institute_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.institute_nirf_ranking || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.mode || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.level || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.letter_no || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{formatDateDDMMYYYY(record.date)}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={record.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {record.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={record.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("session_speaker_details", record.id, "verified")}
                          onUnverify={() => updateVerificationStatus("session_speaker_details", record.id, "unverified")}
                          onReject={() => handleOpenReject("session_speaker_details", record.id)}
                          rejectionReason={record.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* FDP Organized */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🎤 FDP Organized</h3>
        {hasData(filteredFdpOrganized) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Program</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Mode</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Sponsorship</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Duration</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Dates</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyResearch && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredFdpOrganized.map((fdp, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{fdp.title || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.mode || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.sponsorship_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.duration_details || `${formatDateDDMMYYYY(fdp.start_date)} to ${formatDateDDMMYYYY(fdp.end_date)}`}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{formatDateDDMMYYYY(fdp.start_date)} to {formatDateDDMMYYYY(fdp.end_date)}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {fdp.document_link ? (
                        <a href={buildProtectedDocumentUrl(fdp.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">View Document</a>
                      ) : "-"}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={fdp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {fdp.verification_status}
                      </span>
                    </td>
                    {canVerifyResearch && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={fdp.verification_status}
                          canVerify={canVerifyResearch}
                          onVerify={() => updateVerificationStatus("fdp_organized", fdp.id, "verified")}
                          onUnverify={() => updateVerificationStatus("fdp_organized", fdp.id, "unverified")}
                          onReject={() => handleOpenReject("fdp_organized", fdp.id)}
                          rejectionReason={fdp.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* FDP Attended */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📜 FDP Attended</h3>
        {hasData(filteredFdpAttended) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Program</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Institute</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">NIRF Rank</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Mode</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Duration</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Dates</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyFdp && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredFdpAttended.map((fdp, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{fdp.title || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.institute_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.nirf_rank || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.mode || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.duration_details || `${formatDateDDMMYYYY(fdp.start_date)} to ${formatDateDDMMYYYY(fdp.end_date)}`}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{fdp.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{formatDateDDMMYYYY(fdp.start_date)} to {formatDateDDMMYYYY(fdp.end_date)}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {fdp.document_link ? (
                        <a href={buildProtectedDocumentUrl(fdp.document_link)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">View Document</a>
                      ) : "-"}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={fdp.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {fdp.verification_status}
                      </span>
                    </td>
                    {canVerifyFdp && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={fdp.verification_status}
                          canVerify={canVerifyFdp}
                          onVerify={() => updateVerificationStatus("fdp_attended", fdp.id, "verified")}
                          onUnverify={() => updateVerificationStatus("fdp_attended", fdp.id, "unverified")}
                          onReject={() => handleOpenReject("fdp_attended", fdp.id)}
                          rejectionReason={fdp.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Resource person in FDP */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🎤 Resource Person in FDP</h3>
        {hasData(filteredFdpResourcePerson) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Institute</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">NIRF Rank</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Title of Talk</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Mode</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Letter No</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Date</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyFdp && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredFdpResourcePerson.map((record, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {record.institute_name || "N/A"}
                      {record.document_link && (
                        <a href={buildProtectedDocumentUrl(record.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.institute_nirf_ranking || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.title || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.mode || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.letter_no || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{formatDateDDMMYYYY(record.date)}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{record.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={record.verification_status === "verified" ? "text-green-600 font-semibold" : record.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {record.verification_status}
                      </span>
                      {record.verification_status === "rejected" && record.rejection_reason && (
                        <div className="text-[9px] text-red-600 italic mt-0.5">Reason: {record.rejection_reason}</div>
                      )}
                    </td>
                    {canVerifyFdp && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={record.verification_status}
                          canVerify={canVerifyFdp}
                          onVerify={() => updateVerificationStatus("fdp_resource_person", record.id, "verified")}
                          onUnverify={() => updateVerificationStatus("fdp_resource_person", record.id, "unverified")}
                          onReject={() => handleOpenReject("fdp_resource_person", record.id)}
                          rejectionReason={record.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* MOOC E-Content Developed */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📖 MOOC E-Content Developed</h3>
        {hasData(filteredMoocEcontentDeveloped) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Content Title</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Content Type</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Credits</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Semester/Students</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyFdp && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredMoocEcontentDeveloped.map((moo, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {moo.content_title || "N/A"}
                      {moo.document_link && (
                        <a href={buildProtectedDocumentUrl(moo.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{moo.content_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{moo.credits !== null ? moo.credits : "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{moo.semester_students || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{moo.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={moo.verification_status === "verified" ? "text-green-600 font-semibold" : moo.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {moo.verification_status}
                      </span>
                      {moo.verification_status === "rejected" && moo.rejection_reason && (
                        <div className="text-[9px] text-red-600 italic mt-0.5">Reason: {moo.rejection_reason}</div>
                      )}
                    </td>
                    {canVerifyFdp && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={moo.verification_status}
                          canVerify={canVerifyFdp}
                          onVerify={() => updateVerificationStatus("mooc_econtent_developed", moo.id, "verified")}
                          onUnverify={() => updateVerificationStatus("mooc_econtent_developed", moo.id, "unverified")}
                          onReject={() => handleOpenReject("mooc_econtent_developed", moo.id)}
                          rejectionReason={moo.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Swayam MOOC Certifications */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">📜 Swayam MOOC Certifications</h3>
        {hasData(filteredSwayamMoocCertifications) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Course Title</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Agency</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Rank</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Grade/Percentage</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyFdp && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSwayamMoocCertifications.map((swa, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">
                      {swa.course_title || "N/A"}
                      {swa.document_link && (
                        <a href={buildProtectedDocumentUrl(swa.document_link)} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-emerald-600 hover:underline font-medium mt-0.5">📄 View Document</a>
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{swa.agency_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{swa.rank || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{swa.grade_percentage || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{swa.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={swa.verification_status === "verified" ? "text-green-600 font-semibold" : swa.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {swa.verification_status}
                      </span>
                      {swa.verification_status === "rejected" && swa.rejection_reason && (
                        <div className="text-[9px] text-red-600 italic mt-0.5">Reason: {swa.rejection_reason}</div>
                      )}
                    </td>
                    {canVerifyFdp && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={swa.verification_status}
                          canVerify={canVerifyFdp}
                          onVerify={() => updateVerificationStatus("swayam_mooc_certifications", swa.id, "verified")}
                          onUnverify={() => updateVerificationStatus("swayam_mooc_certifications", swa.id, "unverified")}
                          onReject={() => handleOpenReject("swayam_mooc_certifications", swa.id)}
                          rejectionReason={swa.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Technical Skills removed from report */}

      {/* Awards */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🏆 Awards & Achievements</h3>
        {hasData(filteredAwards) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Award</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Level</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Organization</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Academic Year</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Document</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyFdp && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAwards.map((award, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{award.award_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{award.level || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{award.organization_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{award.award_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{award.academic_year || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      {award.letter_details ? (
                        <a href={award.letter_details} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          Supporting Document
                        </a>
                      ) : (
                        "No document link"
                      )}
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={award.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {award.verification_status}
                      </span>
                    </td>
                    {canVerifyFdp && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={award.verification_status}
                          canVerify={canVerifyFdp}
                          onVerify={() => updateVerificationStatus("awards", award.id, "verified")}
                          onUnverify={() => updateVerificationStatus("awards", award.id, "unverified")}
                          onReject={() => handleOpenReject("awards", award.id)}
                          rejectionReason={award.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

      {/* Industry Connect */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">🏢 Industry Connect</h3>
        {hasData(filteredIndustryConnect) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Industry</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Expected</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Achieved</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Status</th>
                  {canVerifyAwards && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredIndustryConnect.map((ind, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{ind.industry_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{ind.outcome_expected || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{ind.outcome_achieved || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={ind.verification_status === "verified" ? "text-green-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {ind.verification_status}
                      </span>
                    </td>
                    {canVerifyAwards && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={ind.verification_status}
                          canVerify={canVerifyAwards}
                          onVerify={() => updateVerificationStatus("industry_connect", ind.id, "verified")}
                          onUnverify={() => updateVerificationStatus("industry_connect", ind.id, "unverified")}
                          onReject={() => handleOpenReject("industry_connect", ind.id)}
                          rejectionReason={ind.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      {/* Professional Memberships */}
      <div className="smooth-scroll-target">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 border-b border-gray-200 pb-0.5 mt-3">👥 Professional Memberships</h3>
        {hasData(filteredProfessionalMemberships) ? (
          <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg p-1">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Society Name</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Membership Type</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Category</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Membership Number</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Active</th>
                  <th className="border border-gray-300 px-1.5 py-0.5 text-left font-semibold">Verification Status</th>
                  {canVerifyAwards && <th className="border border-gray-300 px-1.5 py-0.5 text-center font-semibold print:hidden">Verify</th>}
                </tr>
              </thead>
              <tbody>
                {filteredProfessionalMemberships.map((mem, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-gray-300 px-1.5 py-0.5 font-semibold">{mem.society_name || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{mem.membership_type || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{mem.category || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">{mem.membership_number || "N/A"}</td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={mem.is_active ? "text-green-600 font-semibold" : "text-gray-500 font-semibold"}>
                        {mem.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-1.5 py-0.5">
                      <span className={mem.verification_status === "verified" ? "text-green-600 font-semibold" : mem.verification_status === "rejected" ? "text-red-600 font-semibold" : "text-yellow-600 font-semibold"}>
                        {mem.verification_status || "unverified"}
                      </span>
                    </td>
                    {canVerifyAwards && (
                      <td className="border border-gray-300 px-1.5 py-0.5 text-center print:hidden">
                        <VerificationBadge
                          status={mem.verification_status || "unverified"}
                          canVerify={canVerifyAwards}
                          onVerify={() => updateVerificationStatus("professional_memberships", mem.id, "verified")}
                          onUnverify={() => updateVerificationStatus("professional_memberships", mem.id, "unverified")}
                          onReject={() => handleOpenReject("professional_memberships", mem.id)}
                          rejectionReason={mem.rejection_reason}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1.5 text-xs text-gray-500 italic">
            Details not available
          </div>
        )}
      </div>

        {/* Rejection Reason Modal */}
        {rejectModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 font-sans">❌ Reject Record</h3>
                <p className="text-xs text-gray-500 mt-1 font-sans">
                  Optionally specify a reason for rejecting this record. The user will be notified of this reason.
                </p>
              </div>
              <textarea
                className="w-full min-h-[100px] p-3 border border-gray-300 rounded-xl bg-white text-gray-900 outline-none focus:ring-2 focus:ring-red-500 font-sans text-sm"
                placeholder="e.g. Missing document attachment, Invalid publication year..."
                value={rejectReasonText}
                onChange={(e) => setRejectReasonText(e.target.value)}
              />
              <div className="flex justify-end gap-3 font-sans">
                <button
                  type="button"
                  onClick={() => setRejectModal({ open: false, tableName: "", recordId: "" })}
                  className="px-4 py-2 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReject}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-sm font-semibold rounded-xl text-white transition-colors"
                >
                  Confirm Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FullReport;

function VerificationBadge({ status, canVerify, onVerify, onUnverify, onReject, rejectionReason }) {
  return (
    <div className="mb-2 flex flex-col gap-1.5 items-start">
      <div className="flex items-center gap-2">
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          status === "verified" ? "bg-green-100 text-green-800 border border-green-200" : 
          status === "rejected" ? "bg-red-100 text-red-800 border border-red-200" : 
          "bg-yellow-100 text-yellow-800 border border-yellow-200"
        }`}>
          {status === "verified" ? "Verified" : (status === "rejected" ? "Rejected" : "Unverified")}
        </span>

        {canVerify && (
          <div className="flex items-center gap-1.5">
            {status !== "verified" && (
              <button
                type="button"
                onClick={onVerify}
                className="rounded bg-green-200 hover:bg-green-300 px-2 py-0.5 text-xs font-semibold text-green-900 transition-colors"
              >
                Verify
              </button>
            )}
            {status !== "rejected" && (
              <button
                type="button"
                onClick={onReject}
                className="rounded bg-red-200 hover:bg-red-300 px-2 py-0.5 text-xs font-semibold text-red-900 transition-colors"
              >
                Reject
              </button>
            )}
            {status !== "unverified" && (
              <button
                type="button"
                onClick={onUnverify}
                className="rounded bg-slate-100 hover:bg-slate-200 border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-700 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        )}
      </div>
      {status === "rejected" && rejectionReason && (
        <p className="text-[10px] font-semibold text-red-600 italic bg-red-50 px-2 py-0.5 rounded border border-red-100 w-fit">
          ⚠️ Reason: ${rejectionReason}
        </p>
      )}
    </div>
  );
}
